// src/modules/requerimiento/components/mantenimiento/AgregarEquiposDialog.jsx
// "Agregar equipos" del Trabajo de campo de mantenimiento (reemplaza la etapa de Programación):
// muestra los módems activos de las placas registradas en el requerimiento y asigna los elegidos.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  IconButton, Stack, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BuildIcon from "@mui/icons-material/Build";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { getModemsByRequerimientoPlacas, postAsignarModems } from "../../services/mantenimiento";

const AMBAR = "#D97706";
const AMBAR_OSCURO = "#B45309";

export default function AgregarEquiposDialog({ open, cod, onClose, onAgregados, onToast }) {
  const [modems,   setModems]   = useState([]);
  const [placas,   setPlacas]   = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [elegidos, setElegidos] = useState(new Set());
  const [saving,   setSaving]   = useState(false);

  const cargar = useCallback(async () => {
    if (!cod) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await getModemsByRequerimientoPlacas(cod);
      // Un módem con varios chips llega repetido (uno por chip): se junta en una sola fila.
      const porSim = new Map();
      (Array.isArray(data?.data) ? data.data : []).forEach((m) => {
        const previo = porSim.get(m.num_sim);
        if (!previo) porSim.set(m.num_sim, { ...m, chips: m.numero_chip ? [m.numero_chip] : [] });
        else if (m.numero_chip && !previo.chips.includes(m.numero_chip)) previo.chips.push(m.numero_chip);
      });
      setModems(Array.from(porSim.values()));
      setPlacas(Array.isArray(data?.placas) ? data.placas : []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "No se pudieron buscar los equipos de las placas");
      setModems([]);
    } finally {
      setLoading(false);
    }
  }, [cod]);

  useEffect(() => {
    if (!open) return;
    setElegidos(new Set());
    cargar();
  }, [open, cargar]);

  // Árbol por vehículo: la placa es el título y sus módems cuelgan de ella.
  const grupos = useMemo(() => {
    const mapa = new Map();
    modems.forEach((m) => {
      const placa = String(m.placa || "").trim() || "Sin placa";
      if (!mapa.has(placa)) mapa.set(placa, []);
      mapa.get(placa).push(m);
    });
    return Array.from(mapa.entries());
  }, [modems]);

  const todos = modems.length > 0 && modems.every((m) => elegidos.has(m.num_sim));

  const toggle = (numSim) => setElegidos((prev) => {
    const next = new Set(prev);
    if (next.has(numSim)) next.delete(numSim); else next.add(numSim);
    return next;
  });

  const handleAgregar = async () => {
    const lista = Array.from(elegidos);
    if (!lista.length) return;
    try {
      setSaving(true);
      await postAsignarModems(String(cod), lista);
      onToast?.({ sev: "success", msg: lista.length === 1 ? "Equipo agregado al mantenimiento" : `${lista.length} equipos agregados al mantenimiento` });
      await onAgregados?.(lista);
      onClose?.();
    } catch (e) {
      onToast?.({ sev: "error", msg: e?.response?.data?.error || e?.response?.data?.message || e?.message || "No se pudieron agregar los equipos" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "12px", height: { sm: "80vh" } } }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Box sx={{ width: 44, height: 44, borderRadius: "8px", color: "#fff", display: "grid", placeItems: "center", flexShrink: 0, background: `linear-gradient(135deg, ${AMBAR}, ${AMBAR_OSCURO})` }}>
          <BuildIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800 }}>Agregar equipos</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {placas.length ? `Equipos activos de ${placas.join(", ")}` : "Equipos activos de las placas del requerimiento"}
          </Typography>
        </Box>
        <IconButton size="small" aria-label="Cerrar" onClick={onClose} disabled={saving}><CloseIcon /></IconButton>
      </Stack>

      <DialogContent dividers sx={{ px: 2.5 }}>
        {loading ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
            <CircularProgress size={26} />
            <Typography variant="body2" color="text.secondary">Buscando los equipos de las placas…</Typography>
          </Stack>
        ) : error ? (
          <Alert severity="error" action={<Button size="small" onClick={cargar}>Reintentar</Button>}>{error}</Alert>
        ) : modems.length === 0 ? (
          <Stack alignItems="center" spacing={0.75} sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
            <SearchOffIcon sx={{ fontSize: 40, opacity: 0.6 }} />
            <Typography sx={{ fontWeight: 700, color: "text.primary" }}>No hay equipos para agregar</Typography>
            <Typography variant="body2">
              Las placas del requerimiento no tienen módems activos, o ya están todos en el mantenimiento.
              Si falta una placa, agrégala con "Editar coordinación".
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {modems.length > 1 && (
              <Stack direction="row" justifyContent="flex-end">
                <Button size="small" onClick={() => setElegidos(todos ? new Set() : new Set(modems.map((m) => m.num_sim)))} sx={{ textTransform: "none" }}>
                  {todos ? "Quitar todos" : "Elegir todos"}
                </Button>
              </Stack>
            )}
            {grupos.map(([placa, lista]) => (
              <Box key={placa}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <DirectionsCarIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  <Typography sx={{ fontWeight: 800 }}>{placa}</Typography>
                </Stack>
                <Stack spacing={1}>
                  {lista.map((m) => {
                    const elegido = elegidos.has(m.num_sim);
                    return (
                      <Stack
                        key={m.num_sim}
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        onClick={() => toggle(m.num_sim)}
                        sx={{
                          p: 1.5, borderRadius: "10px", border: "1px solid", cursor: "pointer",
                          borderColor: elegido ? AMBAR : "divider",
                          bgcolor: elegido ? "rgba(217,119,6,0.06)" : "transparent",
                        }}
                      >
                        {elegido
                          ? <CheckCircleIcon sx={{ color: AMBAR }} />
                          : <RadioButtonUncheckedIcon sx={{ color: "text.disabled" }} />}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{m.num_sim}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap component="div">
                            {[
                              m.modelo,
                              m.chips.length ? `Chip ${m.chips.join(", ")}` : null,
                              m.ciudad,
                              m.estado,
                            ].filter(Boolean).join(" · ") || "—"}
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        {!loading && modems.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mr: "auto" }}>
            {elegidos.size === 0 ? "Elige los equipos a revisar." : elegidos.size === 1 ? "1 equipo" : `${elegidos.size} equipos`}
          </Typography>
        )}
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleAgregar}
          disabled={saving || elegidos.size === 0}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {saving ? "Agregando…" : elegidos.size > 1 ? `Agregar ${elegidos.size} equipos` : "Agregar equipo"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
